import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { FeaturedStats } from "@/components/dashboard/FeaturedStats";
import { UpsellBanner } from "@/components/dashboard/UpsellBanner";
import { supabase } from "@/integrations/supabase/client";
import { 
  User, Calendar, Star, DollarSign, Settings, 
  Eye, Award, ArrowRight, CreditCard, Users, LogOut, Sparkles, Building2, Briefcase, Handshake
} from "lucide-react";

interface Stats {
  profileViews: number;
  viewsGrowth: number;
  avgRating: number;
  totalReviews: number;
  eventsCreated: number;
  contacts: number;
  contactsGrowth: number;
  companyAvgRating: number | null;
  companyTotalReviews: number | null;
  companyName: string | null;
}

const Dashboard = () => {
  const { t } = useTranslation();
  const { user, isLoading, isAuthenticated, userRole, isSubscriber, isAdmin, isPro, isPlus, signOut } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<Stats | null>(null);
  const [profileCompletion, setProfileCompletion] = useState(0);
  const [profileCompletionLoaded, setProfileCompletionLoaded] = useState(false);
  const [loadingStats, setLoadingStats] = useState(true);
  const [firstName, setFirstName] = useState<string>("");

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate("/auth/login");
    }
  }, [isLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (user && isSubscriber()) {
      fetchDashboardData();
    } else {
      setLoadingStats(false);
    }
  }, [user]);

  const fetchDashboardData = async () => {
    try {
      // Fetch user profile first (needed for other queries)
      const { data: profile } = await supabase
        .from("profiles")
        .select("id, first_name, business_name, bio_it, phone, address_city, categories, profile_image, views_count")
        .eq("user_id", user?.id)
        .single();

      if (!profile) {
        setLoadingStats(false);
        return;
      }

      // Save first name for display
      setFirstName(profile.first_name || "");
      
      // Calculate profile completion
      const fields = [
        profile.business_name,
        profile.bio_it,
        profile.phone,
        profile.address_city,
        profile.categories?.length > 0,
        profile.profile_image,
      ];
      const completed = fields.filter(f => f).length;
      setProfileCompletion(Math.round((completed / fields.length) * 100));
      setProfileCompletionLoaded(true);

      // Parallel queries for performance
      const [reviewsResult, eventsResult, contactsResult, companyResult] = await Promise.all([
        supabase
          .from("reviews")
          .select("rating")
          .eq("profile_id", profile.id)
          .eq("admin_approved", true),
        supabase
          .from("events")
          .select("*", { count: "exact", head: true })
          .eq("organizer_id", user?.id),
        supabase
          .from("contact_clicks")
          .select("*", { count: "exact", head: true })
          .eq("profile_id", profile.id),
        supabase
          .from("companies")
          .select("id, name")
          .eq("owner_user_id", user?.id)
          .maybeSingle()
      ]);

      const reviews = reviewsResult.data;
      const avgRating = reviews && reviews.length > 0
        ? reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length
        : 0;

      // Use existing views_count from profile
      const currentViews = profile.views_count || 0;
      const lastViews = Math.floor(currentViews * 0.8);
      const viewsGrowth = lastViews > 0 
        ? Math.round(((currentViews - lastViews) / lastViews) * 100) 
        : 0;

      const contactsCount = contactsResult.count || 0;

      // Company rating (only if company exists)
      let companyAvgRating: number | null = null;
      let companyTotalReviews: number | null = null;
      let companyName: string | null = null;

      if (companyResult.data) {
        companyName = companyResult.data.name;
        const { data: companyRatingData } = await supabase
          .rpc("calculate_company_rating", { p_company_id: companyResult.data.id });
        
        if (companyRatingData && companyRatingData.length > 0) {
          companyAvgRating = companyRatingData[0].average_rating ?? 0;
          companyTotalReviews = companyRatingData[0].total_reviews ?? 0;
        } else {
          companyAvgRating = 0;
          companyTotalReviews = 0;
        }
      }

      setStats({
        profileViews: currentViews,
        viewsGrowth,
        avgRating: Math.round(avgRating * 10) / 10,
        totalReviews: reviews?.length || 0,
        eventsCreated: eventsResult.count || 0,
        contacts: contactsCount,
        contactsGrowth: 0, // Simplified - removed extra query
        companyAvgRating,
        companyTotalReviews,
        companyName,
      });
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoadingStats(false);
    }
  };

  interface MenuItem {
    title: string;
    description: string;
    icon: typeof User;
    href: string;
    color: string;
    special?: boolean;
  }

  const getMenuItems = (): MenuItem[] => {
    const baseItems: MenuItem[] = [
      {
        title: t("dashboard.myProfile"),
        description: "Gestisci le tue informazioni personali",
        icon: User,
        href: "/dashboard/profile-edit",
        color: "bg-blue-100 text-blue-600",
      },
    ];

    // Admin button goes FIRST and is special
    if (isAdmin()) {
      baseItems.unshift({
        title: "🛡️ Amministratore",
        description: "Pannello di controllo completo",
        icon: Award,
        href: "/admin/dashboard",
        color: "bg-red-100 text-red-600",
        special: true,
      });
    }

    if (isSubscriber()) {
      baseItems.push(
        {
          title: t("dashboard.subscription"),
          description: t("dashboard.subscriptionDescription"),
          icon: CreditCard,
          href: "/dashboard/subscription",
          color: "bg-orange-100 text-orange-600",
        },
        {
          title: t("dashboard.settings"),
          description: t("dashboard.settingsDescription"),
          icon: Settings,
          href: "/dashboard/preferences",
          color: "bg-gray-100 text-gray-600",
        },
        {
          title: t("partners.title"),
          description: t("partners.exclusiveDescription"),
          icon: Handshake,
          href: "/partners",
          color: "bg-pink-100 text-pink-600",
        }
      );
    }

    return baseItems;
  };

  if (isLoading || loadingStats) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <p className="font-heading text-xl">{t("common.loading") || "Caricamento..."}</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  const menuItems = getMenuItems();

  return (
    <div className="min-h-screen flex flex-col bg-surface">
      <Header />

      <main className="flex-1 container mx-auto px-4 py-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="font-heading text-4xl font-bold mb-2 text-dark">
              {t("dashboard.welcome")}, {firstName || user?.email?.split("@")[0]}!
            </h1>
            <p className="text-muted-foreground">
              {new Date().toLocaleDateString("it-IT", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </p>
          </div>

          {/* Profile Completion Alert */}
          {profileCompletionLoaded && profileCompletion < 100 && isSubscriber() && (
            <div className="mb-8 p-4 bg-warning/10 border-[3px] border-warning">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-heading font-bold mb-2 text-dark">
                    {t('dashboard.onboarding.completeProfileTitle')}
                  </h3>
                  <p className="text-sm text-dark/80">
                    {t('dashboard.onboarding.completeProfileDesc', { completion: profileCompletion })}
                  </p>
                </div>
                <Link
                  to="/dashboard/profile-edit"
                  className="px-4 py-2 bg-warning text-dark font-bold hover:bg-warning/80 transition-colors border-[2px] border-dark"
                >
                  {t('dashboard.onboarding.completeNow')}
                </Link>
              </div>
              <div className="w-full bg-gray-200 h-2 border-[2px] border-dark">
                <div
                  className="bg-warning h-full transition-all"
                  style={{ width: `${profileCompletion}%` }}
                />
              </div>
            </div>
          )}

          {/* Stats Cards - Solo per subscribers */}
          {isSubscriber() && stats && (
            <div className={`grid gap-4 mb-8 ${stats.companyName ? 'md:grid-cols-5' : 'md:grid-cols-4'}`}>
              <div className="bg-white border-[3px] border-dark shadow-[4px_4px_0px_#101322] p-6">
                <div className="flex items-center justify-between mb-2">
                  <Eye className="w-8 h-8 text-primary" />
                  {stats.viewsGrowth > 0 && (
                    <span className="text-xs text-success font-bold">
                      +{stats.viewsGrowth}%
                    </span>
                  )}
                </div>
                <div className="text-2xl font-bold text-dark">{stats.profileViews}</div>
                <div className="text-sm text-muted-foreground">
                  {t("dashboard.stats.profileViews")}
                </div>
              </div>

              <div className="bg-white border-[3px] border-dark shadow-[4px_4px_0px_#101322] p-6">
                <div className="flex items-center justify-between mb-2">
                  <Star className="w-8 h-8 text-yellow-500" />
                  <span className="text-xs text-muted-foreground">
                    {stats.totalReviews} {t("dashboard.stats.reviews", "recensioni")}
                  </span>
                </div>
                <div className="text-2xl font-bold text-dark">{stats.avgRating}/5</div>
                <div className="text-sm text-muted-foreground">
                  {t("dashboard.stats.avgRating")}
                </div>
              </div>

              <div className="bg-white border-[3px] border-dark shadow-[4px_4px_0px_#101322] p-6">
                <div className="flex items-center justify-between mb-2">
                  <Calendar className="w-8 h-8 text-purple-500" />
                  <span className="text-xs text-muted-foreground">
                    {t("dashboard.stats.thisMonth")}
                  </span>
                </div>
                <div className="text-2xl font-bold text-dark">{stats.eventsCreated}</div>
                <div className="text-sm text-muted-foreground">
                  {t("dashboard.stats.eventsCreated")}
                </div>
              </div>

              <div className="bg-white border-[3px] border-dark shadow-[4px_4px_0px_#101322] p-6">
                <div className="flex items-center justify-between mb-2">
                  <Users className="w-8 h-8 text-blue-500" />
                  {stats.contactsGrowth > 0 && (
                    <span className="text-xs text-success font-bold">
                      +{stats.contactsGrowth}%
                    </span>
                  )}
                </div>
                <div className="text-2xl font-bold text-dark">{stats.contacts}</div>
                <div className="text-sm text-muted-foreground">{t("dashboard.stats.contactClicks")}</div>
              </div>

              {/* Company Rating Box - Solo se l'utente ha un'azienda */}
              {stats.companyName && (
                <div className="bg-white border-[3px] border-dark shadow-[4px_4px_0px_#101322] p-6">
                  <div className="flex items-center justify-between mb-2">
                    <Building2 className="w-8 h-8 text-teal-500" />
                    <span className="text-xs text-muted-foreground">
                      {stats.companyTotalReviews || 0} {t("dashboard.stats.reviews", "recensioni")}
                    </span>
                  </div>
                  <div className="text-2xl font-bold text-dark">
                    {stats.companyAvgRating ?? 0}/5
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {t("dashboard.stats.companyRating", "Rating azienda")}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Featured Stats - Solo per spotlight/social_boost */}
          {isSubscriber() && (
            <div className="mb-8">
              <FeaturedStats />
            </div>
          )}

          {/* Upsell Banner - Solo quando profilo completo */}
          {profileCompletionLoaded && profileCompletion === 100 && isSubscriber() && (
            <UpsellBanner />
          )}

        {/* Main Action Cards - Solo per subscribers */}
        {isSubscriber() && (
          <div className="mb-8">
            <h2 className="font-heading text-2xl font-bold mb-4 text-dark">
              {t("dashboard.mainActions")}
            </h2>
            
            {/* Prima riga: Invita Amici, Creator, Guadagni */}
            <div className="grid md:grid-cols-3 gap-6 mb-6">
              {/* Invita Amici Card */}
              <Link
                to="/dashboard/affiliate"
                className="bg-white border-[3px] border-dark shadow-[6px_6px_0px_#101322] p-6 hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[8px_8px_0px_#101322] transition-all group"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="p-3 rounded-lg bg-blue-100 text-blue-600">
                    <Users className="w-6 h-6" />
                  </div>
                  <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                </div>
                <h3 className="font-heading text-xl font-bold mb-2 text-dark">
                  {t("affiliate.title")}
                </h3>
                <p className="text-muted-foreground text-sm">
                  {t("dashboard.affiliateDescription")}
                </p>
              </Link>

              {/* Creator Card */}
              <Link
                to="/dashboard/creator"
                className="bg-white border-[3px] border-dark shadow-[6px_6px_0px_#101322] p-6 hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[8px_8px_0px_#101322] transition-all group"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="p-3 rounded-lg bg-purple-100 text-purple-600">
                    <Sparkles className="w-6 h-6" />
                  </div>
                  <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                </div>
                <h3 className="font-heading text-xl font-bold mb-2 text-dark">
                  {t("creator.title")}
                </h3>
                <p className="text-muted-foreground text-sm">
                  {t("dashboard.creatorDescription")}
                </p>
              </Link>

              {/* Guadagni Card */}
              <Link
                to="/dashboard/earnings"
                className="bg-white border-[3px] border-dark shadow-[6px_6px_0px_#101322] p-6 hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[8px_8px_0px_#101322] transition-all group"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="p-3 rounded-lg bg-green-100 text-green-600">
                    <DollarSign className="w-6 h-6" />
                  </div>
                  <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                </div>
                <h3 className="font-heading text-xl font-bold mb-2 text-dark">
                  {t("earnings.title")}
                </h3>
                <p className="text-muted-foreground text-sm">
                  {t("dashboard.earningsDescription")}
                </p>
              </Link>
            </div>

            {/* Seconda riga: Eventi, Recensioni, Annunci di Lavoro */}
            <div className={`grid gap-6 ${
              isPlus() 
                ? 'md:grid-cols-3'   // Plus: Eventi + Recensioni + Lavoro
                : isPro() 
                  ? 'md:grid-cols-2' // Pro: Eventi + Recensioni
                  : 'md:grid-cols-1' // Base: solo Recensioni
            }`}>
              {/* Eventi Card - solo per Pro e Plus */}
              {isPro() && (
                <Link
                  to="/dashboard/events"
                  className="bg-white border-[3px] border-dark shadow-[6px_6px_0px_#101322] p-6 hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[8px_8px_0px_#101322] transition-all group"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 rounded-lg bg-purple-100 text-purple-600">
                      <Calendar className="w-6 h-6" />
                    </div>
                    <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                  <h3 className="font-heading text-xl font-bold mb-2 text-dark">
                    {t("dashboard.events")}
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    {t("dashboard.eventsDescription")}
                  </p>
                </Link>
              )}

              {/* Recensioni Card */}
              <Link
                to="/dashboard/reviews"
                className="bg-white border-[3px] border-dark shadow-[6px_6px_0px_#101322] p-6 hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[8px_8px_0px_#101322] transition-all group"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="p-3 rounded-lg bg-yellow-100 text-yellow-600">
                    <Star className="w-6 h-6" />
                  </div>
                  <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                </div>
                <h3 className="font-heading text-xl font-bold mb-2 text-dark">
                  {t("dashboard.reviews")}
                </h3>
                <p className="text-muted-foreground text-sm">
                  {t("dashboard.reviewsDescription")}
                </p>
              </Link>

              {/* Annunci di Lavoro Card - solo per Plus */}
              {isPlus() && (
                <Link
                  to="/dashboard/company/jobs"
                  className="bg-white border-[3px] border-dark shadow-[6px_6px_0px_#101322] p-6 hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[8px_8px_0px_#101322] transition-all group"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 rounded-lg bg-blue-100 text-blue-600">
                      <Briefcase className="w-6 h-6" />
                    </div>
                    <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                  <h3 className="font-heading text-xl font-bold mb-2 text-dark">
                    {t("dashboard.jobPostings")}
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    {t("dashboard.jobPostingsDescription")}
                  </p>
                </Link>
              )}
            </div>
          </div>
        )}

          {/* Menu Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <h2 className="font-heading text-2xl font-bold mb-4 text-dark col-span-full">
              {t("dashboard.otherSections")}
            </h2>
            {menuItems.map((item, index) => {
              const Icon = item.icon;
              const isSpecial = item.special || false;
              return (
                <Link
                  key={index}
                  to={item.href}
                  className={`
                    ${isSpecial ? 'col-span-full' : ''}
                    bg-white border-[3px]
                    ${isSpecial ? 'border-error shadow-[8px_8px_0px_#D22B2B] bg-gradient-to-br from-red-50 to-white' : 'border-dark shadow-[6px_6px_0px_#101322]'}
                    p-6 hover:translate-x-[-2px] hover:translate-y-[-2px]
                    ${isSpecial ? 'hover:shadow-[10px_10px_0px_#D22B2B]' : 'hover:shadow-[8px_8px_0px_#101322]'}
                    transition-all group
                  `}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className={`p-3 rounded-lg ${item.color} ${isSpecial ? 'border-[2px] border-error' : ''}`}>
                      <Icon className={`${isSpecial ? 'w-8 h-8' : 'w-6 h-6'}`} />
                    </div>
                    <ArrowRight className={`${isSpecial ? 'w-8 h-8' : 'w-5 h-5'} text-muted-foreground group-hover:text-primary transition-colors`} />
                  </div>
                  <h3 className={`font-heading ${isSpecial ? 'text-2xl' : 'text-xl'} font-bold mb-2 text-dark`}>
                    {item.title}
                  </h3>
                  <p className={`text-muted-foreground ${isSpecial ? 'text-base' : 'text-sm'}`}>{item.description}</p>
                </Link>
              );
            })}
          </div>

          {/* Quick Actions */}
          <div className="bg-white border-[3px] border-dark shadow-[6px_6px_0px_#101322] p-6">
            <h2 className="font-heading text-2xl font-bold mb-4 text-dark">
              {t("dashboard.quickActions")}
            </h2>
            <div className="grid md:grid-cols-5 gap-4">
              {isSubscriber() ? (
                <>
                  <Link
                    to="/dashboard/profile-edit"
                    className="flex items-center justify-center gap-2 min-h-[56px] px-4 py-3 bg-surface text-dark uppercase tracking-wider hover:bg-primary hover:text-dark hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px] hover:shadow-dark transition-all font-semibold border-[3px] border-dark shadow-[4px_4px_0px_0px] shadow-dark"
                  >
                    {t("dashboard.completeProfile")}
                  </Link>
                  <Link
                    to="/dashboard/events/create"
                    className="flex items-center justify-center gap-2 min-h-[56px] px-4 py-3 bg-surface text-dark uppercase tracking-wider hover:bg-primary hover:text-dark hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px] hover:shadow-dark transition-all font-semibold border-[3px] border-dark shadow-[4px_4px_0px_0px] shadow-dark"
                  >
                    {t("dashboard.createEvent")}
                  </Link>
                  <Link
                    to="/dashboard/affiliate"
                    className="flex items-center justify-center gap-2 min-h-[56px] px-4 py-3 bg-surface text-dark uppercase tracking-wider hover:bg-primary hover:text-dark hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px] hover:shadow-dark transition-all font-semibold border-[3px] border-dark shadow-[4px_4px_0px_0px] shadow-dark"
                  >
                    {t("dashboard.inviteFriends")}
                  </Link>
                  <button
                    onClick={() => {
                      if (navigator.share) {
                        navigator.share({
                          title: "Il mio profilo su Inclusia",
                          url: `${window.location.origin}/professional/me`,
                        });
                      }
                    }}
                    className="flex items-center justify-center gap-2 min-h-[56px] px-4 py-3 bg-surface text-dark uppercase tracking-wider hover:bg-primary hover:text-dark hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px] hover:shadow-dark transition-all font-semibold border-[3px] border-dark shadow-[4px_4px_0px_0px] shadow-dark"
                  >
                    {t("dashboard.shareProfile")}
                  </button>
                  <button
                    onClick={() => signOut()}
                    className="flex items-center justify-center gap-2 min-h-[56px] px-4 py-3 bg-red-500 text-white uppercase tracking-wider hover:bg-red-600 hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px] hover:shadow-dark transition-all font-semibold border-[3px] border-dark shadow-[4px_4px_0px_0px] shadow-dark"
                  >
                    <LogOut className="w-4 h-4" />
                    {t("common.logout")}
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/become-professional"
                    className="flex items-center justify-center gap-2 min-h-[56px] px-4 py-3 bg-primary text-dark uppercase tracking-wider hover:bg-primary/80 hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px] hover:shadow-dark transition-all font-semibold border-[3px] border-dark shadow-[4px_4px_0px_0px] shadow-dark"
                  >
                    Diventa professionista
                  </Link>
                  <Link
                    to="/professionals"
                    className="flex items-center justify-center gap-2 min-h-[56px] px-4 py-3 bg-surface text-dark uppercase tracking-wider hover:bg-primary hover:text-dark hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px] hover:shadow-dark transition-all font-semibold border-[3px] border-dark shadow-[4px_4px_0px_0px] shadow-dark"
                  >
                    Trova professionisti
                  </Link>
                  <Link
                    to="/events"
                    className="flex items-center justify-center gap-2 min-h-[56px] px-4 py-3 bg-surface text-dark uppercase tracking-wider hover:bg-primary hover:text-dark hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px] hover:shadow-dark transition-all font-semibold border-[3px] border-dark shadow-[4px_4px_0px_0px] shadow-dark"
                  >
                    Eventi vicino a te
                  </Link>
                  <Link
                    to="/faq"
                    className="flex items-center justify-center gap-2 min-h-[56px] px-4 py-3 bg-surface text-dark uppercase tracking-wider hover:bg-primary hover:text-dark hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px] hover:shadow-dark transition-all font-semibold border-[3px] border-dark shadow-[4px_4px_0px_0px] shadow-dark"
                  >
                    Centro assistenza
                  </Link>
                  <button
                    onClick={() => signOut()}
                    className="flex items-center justify-center gap-2 min-h-[56px] px-4 py-3 bg-red-500 text-white uppercase tracking-wider hover:bg-red-600 hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px] hover:shadow-dark transition-all font-semibold border-[3px] border-dark shadow-[4px_4px_0px_0px] shadow-dark"
                  >
                    <LogOut className="w-4 h-4" />
                    {t("common.logout")}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Dashboard;
